<template>
  <div
    class="rounded-lg overflow-hidden shadow-xl font-medium text-base flex items-stretch pointer-events-auto relative"
    :class="[classes.wrapper[type], animationClass]"
    @focusin="handleFocusIn"
    @focusout="handleFocusOut"
    tabindex="0"
  >
    <div
      class="absolute inset-0 pointer-events-none transition-opacity duration-200"
      :class="[{'opacity-0 ease-in': isFocused, 'opacity-15 ease-out': !isFocused}]"
      role="presentation"
    >
      <div
        ref="loadingBarRef"
        class="h-full origin-left bg-gradient-to-r"
        :class="[classes.loadingBar[type], {'animate-loading-bar': !isFocused || isPaused}]"
        :style="{
          animationDuration: `${duration}ms`,
          animationPlayState: isPaused ? 'paused' : 'running',
        }"
        role="presentation"
      ></div>
    </div>
    <div class="w-2" :class="classes.crust[type]" role="presentation"></div>
    <div
      class="relative z-1 flex-1 px-4 py-3 border-2 rounded-r-lg flex items-center gap-2"
      :class="classes.content[type]"
    >
      <s-icon
        :name="icon || icons[type]"
        size="22"
        :class="classes.icon[type]"
        aria-hidden="true"
      />
      <span class="flex-1">
        <slot />
      </span>
      <button
        class="p-2 -m-2 cursor-pointer opacity-50 hover:opacity-100 transition-opacity ease-out duration-150"
        @click="handleDismiss"
      >
        <s-icon name="close" size="20" aria-hidden="true" />
        <span class="sr-only">{{ t('actions.dismiss') }}</span>
      </button>
    </div>
  </div>
</template>
<script setup lang="ts">
import {withDefaults, defineProps, defineEmits, ref, onMounted, onBeforeUnmount} from 'vue';
import SIcon from '../design-system/SIcon.vue';
import {useI18n} from 'vue-i18n';

type AlertType = 'success' | 'warning' | 'error';

const {t} = useI18n({
  inheritLocale: true,
  useScope: 'local',
});

const props = withDefaults(
  defineProps<{
    type?: AlertType;
    icon?: string;
    duration?: number;
  }>(),
  {
    type: 'success' as AlertType,
    duration: 7500,
  }
);

const emit = defineEmits<{
  (e: 'dismiss'): void;
}>();

const isPaused = ref(false);
const loadingBarRef = ref<HTMLElement | null>(null);
const animationClass = ref('animate-toast');
const isFocused = ref(false);
const isDismissed = ref(false);
let timeoutId: number | undefined;

const handleFocusIn = () => {
  isFocused.value = true;
  isPaused.value = true;
  if (timeoutId) {
    clearTimeout(timeoutId);
  }
};

const handleFocusOut = () => {
  isFocused.value = false;
  if (!isDismissed.value) {
    const element = loadingBarRef.value;
    if (element) {
      element.classList.remove('animate-loading-bar');
      void element.offsetWidth;
      element.classList.add('animate-loading-bar');
    }
    startTimer();
  }
};

const startTimer = () => {
  if (props.duration > 0) {
    isPaused.value = false;
    timeoutId = window.setTimeout(() => {
      handleDismiss();
    }, props.duration);
  }
};

const handleDismiss = () => {
  isDismissed.value = true;
  animationClass.value = 'animate-toast-out';
  if (timeoutId) {
    clearTimeout(timeoutId);
  }
  setTimeout(() => {
    emit('dismiss');
  }, 400);
};

onMounted(() => {
  startTimer();
});

onBeforeUnmount(() => {
  if (timeoutId) {
    clearTimeout(timeoutId);
  }
});

const classes: Record<
  'wrapper' | 'crust' | 'content' | 'icon' | 'loadingBar',
  Record<AlertType, string>
> = {
  wrapper: {
    success: 'bg-green-100',
    warning: 'bg-orange-100',
    error: 'bg-red-100',
  },
  crust: {
    success: 'bg-green-400',
    warning: 'bg-orange-400',
    error: 'bg-red-400',
  },
  content: {
    success: 'border-green-200 text-green-700',
    warning: 'border-orange-200 text-orange-700',
    error: 'border-red-300 text-red-700',
  },
  icon: {
    success: 'text-green-500',
    warning: 'text-orange-500',
    error: 'text-red-500',
  },
  loadingBar: {
    success: 'from-green-400/0 to-green-500',
    warning: 'from-orange-400/0 to-orange-500',
    error: 'from-red-400/0 to-red-500',
  },
};

const icons: Record<AlertType, string> = {
  success: 'check-bold',
  warning: 'alert',
  error: 'alert',
};
</script>
<i18n>
  {
    "en": {
      "actions": {
        "dismiss": "Dismiss message"
      }
    },
    "fr": {
      "actions": {
        "dismiss": "Fermer le message"
      }
    }
  }
</i18n>
